import * as THREE from "three";
import { toCreasedNormals } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { PlinkoLayout } from "./plinko_layout.js";

export interface HappyFallBallPose
{
  x: number;
  y: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
  scale: number;
  shade: number;
}

export interface HappyFallSceneFrame
{
  x: number;
  y: number;
  w: number;
  h: number;
  cannon_y: number;
}

export class HappyFall3D
{
  private static readonly FOV = 30;
  private static readonly CANNON_LENGTH = 530;
  private static readonly CANNON_PITCH = THREE.MathUtils.degToRad( 62 );
  private static readonly CANNON_Z = 300;
  private static readonly AMBIENT = 0.55;
  private static readonly SUN = 1.0;
  private static readonly SUN_POSITION = new THREE.Vector3( -400, 900, 1600 );
  private static readonly CANNON_LIGHT = 0.9;
  private static readonly CANNON_LIGHT_OFFSET = new THREE.Vector3( 200, 400, 1200 );
  private static readonly CANNON_GLOW = 0.75;
  private static readonly CANNON_SHININESS = 60;
  private static readonly CANNON_SPECULAR = 0x666666;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly view_center: THREE.Vector2;
  private readonly view_distance: number;
  private readonly cannon_yaw = new THREE.Group();
  private readonly ball_template: THREE.Object3D;
  private readonly ball_material: THREE.MeshLambertMaterial;
  private readonly balls: THREE.Object3D[] = [];
  private readonly ball_materials: THREE.MeshLambertMaterial[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];

  constructor( width: number, height: number, pixel_ratio: number, frame: HappyFallSceneFrame )
  {
    this.renderer = new THREE.WebGLRenderer( { alpha: true, antialias: true, premultipliedAlpha: true } );
    this.renderer.setClearColor( 0x000000, 0 );
    this.renderer.setPixelRatio( pixel_ratio );
    this.renderer.setSize( width, height, false );

    const center_x = frame.x + frame.w / 2;
    const center_y = -( frame.y + frame.h / 2 );
    const distance = frame.h / 2 / Math.tan( THREE.MathUtils.degToRad( HappyFall3D.FOV / 2 ) );
    this.camera = new THREE.PerspectiveCamera( HappyFall3D.FOV, frame.w / frame.h, distance * 0.5, distance * 2 );
    this.camera.position.set( center_x, center_y, distance );
    this.camera.lookAt( center_x, center_y, 0 );
    this.view_center = new THREE.Vector2( center_x, center_y );
    this.view_distance = distance;

    const sun = new THREE.DirectionalLight( 0xffffff, HappyFall3D.SUN );
    sun.position.copy( HappyFall3D.SUN_POSITION );
    this.scene.add( sun );
    this.scene.add( new THREE.AmbientLight( 0xffffff, HappyFall3D.AMBIENT ) );

    this.createCannon( frame.cannon_y );

    this.ball_material = new THREE.MeshLambertMaterial( {
      map: ResourceManager.getTexture( ResourceID.CannonBaseMap ),
      normalMap: ResourceManager.getNormalMap( ResourceID.CannonNormalMap ),
      normalScale: new THREE.Vector2( 0.3, 0.3 ),
    } );
    this.materials.push( this.ball_material );
    this.ball_template = this.createBallTemplate();
  }

  private createCannon( pivot_y: number ): void
  {
    const visual = ResourceManager.getModel( ResourceID.CannonModel );
    visual.updateMatrixWorld( true );

    const size = new THREE.Box3().setFromObject( visual ).getSize( new THREE.Vector3() );
    visual.scale.setScalar( HappyFall3D.CANNON_LENGTH / Math.max( size.x, size.y, size.z ) );

    const base_map = ResourceManager.getTexture( ResourceID.CannonModelBaseMap );
    const material = new THREE.MeshPhongMaterial( {
      map: base_map,
      normalMap: ResourceManager.getNormalMap( ResourceID.CannonModelNormalMap ),
      normalScale: new THREE.Vector2( 0.1, 0.1 ),
      emissive: new THREE.Color( 0xffffff ),
      emissiveMap: base_map,
      emissiveIntensity: HappyFall3D.CANNON_GLOW,
      shininess: HappyFall3D.CANNON_SHININESS,
      specular: new THREE.Color( HappyFall3D.CANNON_SPECULAR ),
    } );
    this.materials.push( material );

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      child.geometry = toCreasedNormals( child.geometry.clone(), THREE.MathUtils.degToRad( 115 ) );
      this.geometries.push( child.geometry );
      child.material = material;
    } );

    visual.updateMatrixWorld( true );
    const box = new THREE.Box3().setFromObject( visual );
    const center = box.getCenter( new THREE.Vector3() );
    visual.position.set( -center.x, -center.y, -box.max.z );

    const pitch = new THREE.Group();
    pitch.rotation.x = HappyFall3D.CANNON_PITCH;
    pitch.add( visual );

    this.cannon_yaw.add( pitch );
    this.cannon_yaw.position.set( PlinkoLayout.CANNON_PIVOT_X, -pivot_y, HappyFall3D.CANNON_Z );
    this.scene.add( this.cannon_yaw );

    const light = new THREE.DirectionalLight( 0xffffff, HappyFall3D.CANNON_LIGHT );
    light.position.copy( this.cannon_yaw.position ).add( HappyFall3D.CANNON_LIGHT_OFFSET );
    light.target = this.cannon_yaw;
    this.scene.add( light );
  }

  private createBallTemplate(): THREE.Object3D
  {
    const model = ResourceManager.getModel( ResourceID.CannonBallModel );
    const size = new THREE.Box3().setFromObject( model ).getSize( new THREE.Vector3() );
    model.scale.setScalar( PlinkoLayout.BALL_RADIUS * 2 / Math.max( size.x, size.y, size.z ) );

    model.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
        child.material = this.ball_material;
    } );

    model.updateMatrixWorld( true );
    const center = new THREE.Box3().setFromObject( model ).getCenter( new THREE.Vector3() );
    model.position.sub( center );

    const holder = new THREE.Group();
    holder.add( model );
    return holder;
  }

  public warmUp(): void
  {
    this.render( [ { x: 0, y: 0, z: 0, rx: 0, ry: 0, rz: 0, scale: 0, shade: 1 } ] );
    this.render( [] );
  }

  public setCannonAngle( angle: number ): void
  {
    this.cannon_yaw.rotation.z = -angle;
  }

  public render( balls: readonly HappyFallBallPose[], show_cannon = true ): HTMLCanvasElement
  {
    this.cannon_yaw.visible = show_cannon;

    while ( this.balls.length < balls.length )
    {
      const ball = this.ball_template.clone();
      const material = this.ball_material.clone();
      ball.traverse( child =>
      {
        if ( child instanceof THREE.Mesh )
          child.material = material;
      } );
      this.ball_materials.push( material );
      this.materials.push( material );
      this.balls.push( ball );
      this.scene.add( ball );
    }

    this.balls.forEach( ( mesh, index ) =>
    {
      const pose = balls[index];
      mesh.visible = pose !== undefined;
      if ( !pose )
        return;

      const depth = ( this.view_distance - pose.z ) / this.view_distance;
      const x = this.view_center.x + ( pose.x - this.view_center.x ) * depth;
      const y = this.view_center.y + ( -pose.y - this.view_center.y ) * depth;
      mesh.position.set( x, y, pose.z );
      mesh.rotation.set( pose.rx, pose.ry, pose.rz );
      mesh.scale.setScalar( pose.scale );
      this.ball_materials[index].color.setScalar( pose.shade );
    } );

    this.renderer.render( this.scene, this.camera );
    return this.renderer.domElement;
  }

  public dispose(): void
  {
    for ( const material of this.materials )
      material.dispose();

    for ( const geometry of this.geometries )
      geometry.dispose();

    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
